import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, FormControl, FormLabel, Input, Button, HStack, Icon, Badge, List, ListItem, ListIcon, Image } from "@chakra-ui/react";
import { FaCheckCircle, FaPhone, FaShieldAlt, FaClock } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarQuote: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // In production, this would send data to backend
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/quote"
        fallbackTitle="Get Solar Quote"
        fallbackDescription="Request a personalized solar installation quote from our experts"
      />
      <NavbarWithCallToAction />

      {!submitted ? (
        <Container maxW="container.sm" py={16}>
          <Box
            as="form"
            onSubmit={handleSubmit}
            p={{ base: 8, md: 10 }}
            bg="white"
            borderRadius="2xl"
            shadow="xl"
            border="2px solid"
            borderColor="gray.200"
          >
            <VStack spacing={6} align="stretch">
              <VStack spacing={2} textAlign="center" pb={2}>
                <Text fontSize="xs" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="wider">
                  Get Started
                </Text>
                <Heading size="xl" fontWeight="bold" color={NAVY}>
                  Get Your Free Solar Quote
                </Heading>
                <Text fontSize="md" color="gray.600" maxW="sm">
                  Please fill out your details below and one of our highly trained solar advisors will be in touch to give you a tailored quote.
                </Text>
              </VStack>

              <Image
                src="/assets/solar-assets/one-solar-team-image-1024x658.jpg"
                alt="One Solar team"
                borderRadius="xl"
                w="full"
                objectFit="cover"
              />

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={NAVY}>Name</FormLabel>
                <Input placeholder="Your full name" size="lg" bg="#D2D4D6" _placeholder={{ color: "gray.600" }} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={NAVY}>Installation Address</FormLabel>
                <Input placeholder="Property address" size="lg" bg="#D2D4D6" _placeholder={{ color: "gray.600" }} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={NAVY}>Phone</FormLabel>
                <Input type="tel" placeholder="0400 000 000" size="lg" bg="#D2D4D6" _placeholder={{ color: "gray.600" }} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color={NAVY}>Email</FormLabel>
                <Input type="email" placeholder="you@example.com" size="lg" bg="#D2D4D6" _placeholder={{ color: "gray.600" }} />
              </FormControl>

              <Button
                type="submit"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                size="lg"
                width="full"
                rightIcon={<Icon as={FaCheckCircle} />}
                mt={2}
              >
                Get My Free Quote
              </Button>

              <HStack justify="center" spacing={2} pt={1}>
                <Icon as={FaPhone} color={NAVY} boxSize={3} />
                <Text fontSize="sm" color="gray.500">
                  Prefer to talk to us directly?{" "}
                  <Text
                    as="a"
                    href="tel:1300663765"
                    fontWeight="700"
                    color={NAVY}
                    _hover={{ color: SOLAR_GOLD }}
                  >
                    Call 1300 663 765
                  </Text>
                </Text>
              </HStack>
            </VStack>
          </Box>
        </Container>
      ) : (
        <Container maxW="container.md" py={16}>
          <Box
            p={12}
            bg="gray.50"
            borderRadius="2xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
            textAlign="center"
          >
            <VStack spacing={6}>
              <Icon as={FaCheckCircle} boxSize={20} color={SOLAR_GOLD} />
              <Heading size="xl" color={NAVY}>
                Thank You for Your Request!
              </Heading>
              <Text fontSize="lg" color="gray.700" maxW="xl">
                We've received your quote request and our solar experts will review it shortly.
              </Text>
              <Box
                p={6}
                bg="white"
                borderRadius="xl"
                width="full"
              >
                <Heading size="md" mb={4}>What Happens Next?</Heading>
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Badge bg={NAVY} color="white" fontSize="lg" p={2} borderRadius="md">1</Badge>
                    <Text textAlign="left">
                      <strong>Within 2 hours:</strong> We'll review your details and may call for clarification
                    </Text>
                  </HStack>
                  <HStack>
                    <Badge bg={NAVY} color="white" fontSize="lg" p={2} borderRadius="md">2</Badge>
                    <Text textAlign="left">
                      <strong>Within 24 hours:</strong> Receive your detailed quote via email
                    </Text>
                  </HStack>
                  <HStack>
                    <Badge bg={NAVY} color="white" fontSize="lg" p={2} borderRadius="md">3</Badge>
                    <Text textAlign="left">
                      <strong>Within 3 days:</strong> Free on-site assessment to confirm design
                    </Text>
                  </HStack>
                  <HStack>
                    <Badge bg={NAVY} color="white" fontSize="lg" p={2} borderRadius="md">4</Badge>
                    <Text textAlign="left">
                      <strong>Installation:</strong> Professional installation within 2-4 weeks
                    </Text>
                  </HStack>
                </VStack>
              </Box>
              <Button
                bg={NAVY}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                size="lg"
                onClick={() => window.location.href = '/solar'}
              >
                Return to Solar Home
              </Button>
            </VStack>
          </Box>
        </Container>
      )}

      {/* Trust Section */}
      {!submitted && (
        <Box bg="gray.50" py={16}>
          <Container maxW="container.lg">
            <Heading size="lg" mb={10} textAlign="center">
              Why Choose Us?
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Box
                p={8}
                bg="white"
                borderRadius="xl"
                textAlign="center"
                shadow="md"
              >
                <Icon as={FaShieldAlt} boxSize={12} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" mb={3}>Quality Guarantee</Heading>
                <List spacing={2} textAlign="left">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    CEC accredited installers
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Tier 1 solar panels
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    25-year warranty
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    10-year workmanship guarantee
                  </ListItem>
                </List>
              </Box>

              <Box
                p={8}
                bg="white"
                borderRadius="xl"
                textAlign="center"
                shadow="md"
              >
                <Icon as={FaClock} boxSize={12} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" mb={3}>Fast Turnaround</Heading>
                <List spacing={2} textAlign="left">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Quote within 24 hours
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Site visit within 3 days
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Installation in 2-4 weeks
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Same-day approvals available
                  </ListItem>
                </List>
              </Box>

              <Box
                p={8}
                bg="white"
                borderRadius="xl"
                textAlign="center"
                shadow="md"
              >
                <Icon as={FaPhone} boxSize={12} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" mb={3}>Expert Support</Heading>
                <List spacing={2} textAlign="left">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Free consultation
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Financing assistance
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Rebate application help
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                    Ongoing maintenance support
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>
          </Container>
        </Box>
      )}

      <SolarFooter />
    </Box>
  );
};

export default SolarQuote;
